import * as React from 'react';
import { createPortal } from 'react-dom';
import { IHostableSurfaceContextProps, HostableSurfaceContext } from './hostable-surface-context';
import { HostableSurface } from './hostable-surface';

export interface IWithHostableSurfaceProps extends Omit<IHostableSurfaceContextProps, 'surfaceElement'> { }

export function withHostableSurface<TProps, TOwnProps extends Omit<TProps, keyof IWithHostableSurfaceProps>>(Component: React.ComponentType<TProps>): React.ComponentType<TOwnProps> {
    return (props: TOwnProps) => {
        const { getSurfaceClientRect, surfaceElement, removeSurfaceEventListener, addSurfaceEventListener } = React.useContext(HostableSurfaceContext);
        return createPortal((
            <Component
                {...props as any}
                getSurfaceClientRect={getSurfaceClientRect}
                addSurfaceEventListener={addSurfaceEventListener}
                removeSurfaceEventListener={removeSurfaceEventListener} />
        ), surfaceElement);
    };
}

export function withHostablePanel<TProps, TOwnProps extends Omit<TProps, keyof IWithHostableSurfaceProps>>(Component: React.ComponentType<TProps>): React.ComponentType<TOwnProps> {
    const ComponentRenderer = withHostableSurface(Component);
    const WithHostablePanel = (props: TOwnProps & { preventClickEventFromBubbling?: boolean }) => {
        const { surfaceElement } = React.useContext(HostableSurfaceContext);
        const { preventClickEventFromBubbling = true } = props;
        return createPortal((
            <HostableSurface position="absolute" preventClickEventFromBubbling={preventClickEventFromBubbling}>
                {React.createElement(ComponentRenderer, props)}
            </HostableSurface>
        ), surfaceElement);
    };
    WithHostablePanel.displayName = `WithHostablePanel(${Component.displayName ?? Component.name})`;
    return WithHostablePanel;
}
